import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiClock, FiCalendar, FiCheckCircle, FiTruck,
  FiAlertTriangle, FiTool, FiChevronRight
} from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const MechanicDashboard = () => {
  const [todayJobs, setTodayJobs] = useState([]);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [stats, setStats] = useState({
    completedToday: 0,
    pendingToday: 0,
    totalWeek: 0,
    rating: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

        const [todayRes, upcomingRes, statsRes] = await Promise.all([
          API.get('/api/bookings/mechanic/my-tasks', {
            params: { date: today }
          }),
          API.get('/api/bookings/mechanic/my-tasks', {
            params: { status: 'confirmed' }
          }),
          API.get('/api/mechanics/dashboard/stats')
        ]);

        // Process today's jobs
        const todayJobs = todayRes.data.map(job => ({
          id: job.id,
          service: job.service_name,
          customerName: job.customer_name,
          vehicle: `${job.make} ${job.model} (${job.license_plate})`,
          time: job.time,
          estimatedTime: job.estimated_time,
          notes: job.notes,
          status: job.task_status
        }));

        // Process upcoming jobs
        const upcomingJobs = upcomingRes.data
          .filter(job => job.date !== today)
          .map(job => ({
            id: job.id,
            date: job.date,
            time: job.time,
            service: job.service_name,
            customerName: job.customer_name,
            vehicle: `${job.make} ${job.model} (${job.license_plate})`
          }));

        setTodayJobs(todayJobs);
        setUpcomingJobs(upcomingJobs);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching mechanic dashboard data:', error);
        setError('Gagal memuat data dashboard. Silakan coba lagi nanti.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const updateJobStatus = async (jobId, status) => {
    try {
      await API.put(`/api/mechanics/task/${jobId}/status`, { status });

      // Update local state
      setTodayJobs(todayJobs.map(job =>
        job.id === jobId ? { ...job, status } : job
      ));
    } catch (error) {
      console.error('Error updating job status:', error);
      alert('Gagal memperbarui status pekerjaan. Silakan coba lagi.');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">
            Menunggu
          </span>
        );
      case 'in-progress':
        return (
          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
            Sedang Dikerjakan
          </span>
        );
      case 'completed':
        return (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
            Selesai
          </span>
        );
      default:
        return null;
    }
  };

  const renderActionButtons = (job) => {
    if (job.status === 'pending') {
      return (
        <button
          onClick={() => updateJobStatus(job.id, 'in-progress')}
          className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
        >
          Mulai Pekerjaan
        </button>
      );
    } else if (job.status === 'in-progress') {
      return (
        <button
          onClick={() => updateJobStatus(job.id, 'completed')}
          className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
        >
          Selesaikan
        </button>
      );
    }
    return null;
  };

  const StatCard = ({ icon, title, value, color }) => (
    <div className="bg-white rounded-lg shadow-md p-5">
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${color} mr-4`}>
          {icon}
        </div>
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout userRole="mechanic" userName="Montir">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="mechanic" userName="Montir">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary">Dashboard Montir</h1>
        <p className="text-gray-600">Selamat datang di panel montir XSignature Auto Garage</p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
          <strong className="font-bold">Error! </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={<FiCheckCircle size={24} className="text-white" />}
          title="Selesai Hari Ini"
          value={stats.completedToday}
          color="bg-green-500"
        />
        <StatCard
          icon={<FiClock size={24} className="text-white" />}
          title="Menunggu Hari Ini"
          value={stats.pendingToday}
          color="bg-yellow-500"
        />
        <StatCard
          icon={<FiCalendar size={24} className="text-white" />}
          title="Total Minggu Ini"
          value={stats.totalWeek}
          color="bg-blue-500"
        />
        <StatCard
          icon={<FiTool size={24} className="text-white" />}
          title="Rating Anda"
          value={`${stats.rating} / 5`}
          color="bg-primary"
        />
      </div>

      {/* Today's Jobs */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-secondary">Pekerjaan Hari Ini</h2>
          <Link to="/mechanic/jobs" className="text-primary hover:text-primary-dark flex items-center text-sm">
            Lihat Semua <FiChevronRight className="ml-1" />
          </Link>
        </div>

        {todayJobs.length === 0 ? (
          <div className="text-center py-8">
            <div className="bg-blue-50 inline-block p-3 rounded-full mb-3">
              <FiCalendar className="text-blue-500" size={24} />
            </div>
            <p className="text-gray-500">Tidak ada pekerjaan untuk hari ini</p>
          </div>
        ) : (
          <div className="space-y-4">
            {todayJobs.map((job) => (
              <div key={job.id} className="border rounded-lg p-4 hover:border-primary transition-colors">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="flex items-start mb-3 md:mb-0">
                    <div className="bg-gray-100 rounded-full p-3 mr-4">
                      <FiTruck size={20} className="text-gray-600" />
                    </div>
                    <div>
                      <div className="flex items-center mb-1">
                        <h3 className="font-medium text-gray-900 mr-3">{job.service}</h3>
                        {getStatusBadge(job.status)}
                      </div>
                      <p className="text-gray-600 text-sm mb-1">{job.customerName} - {job.vehicle}</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <FiClock className="mr-1" />
                        <span>{job.time} ({job.estimatedTime} menit)</span>
                      </div>
                      {job.notes && (
                        <div className="mt-2 text-sm bg-yellow-50 p-2 rounded border border-yellow-100 flex items-start">
                          <FiAlertTriangle className="text-yellow-500 mr-1 mt-0.5 flex-shrink-0" />
                          <span>{job.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ml-11 md:ml-0">
                    {renderActionButtons(job)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming Jobs */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-secondary">Pekerjaan Mendatang</h2>
          <Link to="/mechanic/jobs" className="text-primary hover:text-primary-dark flex items-center text-sm">
            Lihat Semua <FiChevronRight className="ml-1" />
          </Link>
        </div>

        {upcomingJobs.length === 0 ? (
          <div className="text-center py-8">
            <div className="bg-gray-50 inline-block p-3 rounded-full mb-3">
              <FiCalendar className="text-gray-400" size={24} />
            </div>
            <p className="text-gray-500">Tidak ada pekerjaan mendatang</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tanggal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Waktu</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pelanggan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Layanan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kendaraan</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {upcomingJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.date}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.time}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.customerName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.service}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.vehicle}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MechanicDashboard; 